import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import Countdown from './components/Countdown';
import DrawingCanvas, { type DrawingCanvasHandle } from './components/DrawingCanvas';
import { socket } from './socket';
import type {
  AckResponse,
  BlindRevealPayload,
  DrawingReplay,
  ReplayStroke,
  BlindSettings,
  BlindSpotlight,
  BlindState,
  BlindTask,
  Player
} from './types';

interface BlindDrawingRouterProps {
  self: Player;
  room: BlindState;
  task: BlindTask | null;
  notify: (message: string) => void;
}

interface BlindAck extends AckResponse {
  correct?: boolean;
  near?: boolean;
  attemptsLeft?: number;
  points?: number;
  message?: string;
  spotlight?: BlindSpotlight | null;
  reveal?: BlindRevealPayload | null;
}

export default function BlindDrawingRouter({ self, room, task, notify }: BlindDrawingRouterProps) {
  const [spotlight, setSpotlight] = useState<BlindSpotlight | null>(null);
  const [reveal, setReveal] = useState<BlindRevealPayload | null>(null);

  useEffect(() => {
    const onSpotlight = (payload: BlindSpotlight) => {
      setSpotlight(payload);
      setReveal(null);
    };
    const onReveal = (payload: BlindRevealPayload) => {
      setSpotlight(payload);
      setReveal(payload);
    };
    socket.on('blind:spotlight', onSpotlight);
    socket.on('blind:reveal', onReveal);
    return () => {
      socket.off('blind:spotlight', onSpotlight);
      socket.off('blind:reveal', onReveal);
    };
  }, []);

  useEffect(() => {
    if (room.phase === 'blind_lobby' || room.phase === 'blind_drawing' || room.phase === 'blind_results') {
      setSpotlight(null);
      setReveal(null);
      return;
    }
    let cancelled = false;
    emitBlindAck('blind:sync', {}).then((response) => {
      if (cancelled || !response.ok) return;
      if (response.spotlight) setSpotlight(response.spotlight);
      if (response.reveal) setReveal(response.reveal);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [room.phase, room.currentDrawingId]);

  if (room.phase === 'blind_lobby') return <BlindLobby self={self} room={room} notify={notify} />;
  if (room.phase === 'blind_drawing') {
    return self.isSpectator
      ? <BlindSpectator room={room} title="الفنانون يرسمون الآن" text="لا أحد يرى خطوطه بعد البداية. عند انتهاء الرسم ستبدأ مرحلة فك الشفرة." />
      : <BlindDrawingStage self={self} room={room} task={task?.kind === 'blind-draw' ? task : null} notify={notify} />;
  }
  if (room.phase === 'blind_guessing') {
    return <BlindGuessStage self={self} room={room} task={task?.kind === 'blind-guess' ? task : null} spotlight={spotlight} notify={notify} />;
  }
  if (room.phase === 'blind_reveal') {
    return <BlindRevealStage self={self} room={room} reveal={reveal} spotlight={spotlight} notify={notify} />;
  }
  if (room.phase === 'blind_results') return <BlindResults self={self} room={room} notify={notify} />;
  return <BlindSpectator room={room} title="تجهيز اللعبة" text="لحظات ويتم تجهيز الجولة التالية." />;
}

function BlindLobby({ self, room, notify }: { self: Player; room: BlindState; notify: (message: string) => void }) {
  const [settings, setSettings] = useState<BlindSettings>(room.blindSettings);
  const [customPrompts, setCustomPrompts] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => setSettings(room.blindSettings), [room.blindSettings]);

  const connectedPlayers = room.players.filter((player) => player.connected && !player.isSpectator);
  const canStart = connectedPlayers.length >= room.minPlayers;
  const patch = <K extends keyof BlindSettings>(key: K, value: BlindSettings[K]) => setSettings((current) => ({ ...current, [key]: value }));

  const save = async () => {
    const response = await emitBlindAck('chef:blindSettings', settings);
    if (!response.ok) throw new Error(response.error || 'تعذر حفظ الإعدادات.');
  };

  const saveSettings = async () => {
    try {
      await save();
      notify('تم حفظ إعدادات Blind Drawing.');
    } catch (error) { notify(messageOf(error)); }
  };

  const start = async () => {
    setBusy(true);
    try {
      await save();
      const customWords = customPrompts.split(/\r?\n/).map((value) => value.trim()).filter(Boolean).slice(0, 100);
      const response = await emitBlindAck('chef:blindStart', { customWords });
      if (!response.ok) throw new Error(response.error || 'تعذر بدء اللعبة.');
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };

  return (
    <section className="blind-lobby">
      <header className="blind-hero glass-card">
        <div className="blind-hero-copy">
          <span className="blind-kicker">🎨 BLIND DRAWING</span>
          <h1>ارسم… ثم <em>لا تنظر!</em></h1>
          <p>كل لاعب يحصل على موقف مضحك. بعد أول لمسة لديك لحظة واحدة فقط لترى خطوطك، ثم تختفي اللوحة بينما يستمر القلم بالتسجيل. بعدها يحاول الجميع فك الكارثة.</p>
          <div className="blind-rule-chips"><span>🙈 رسم أعمى حقيقي</span><span>⚡ الجميع يرسم معًا</span><span>🎯 3 محاولات ذكية</span><span>🏆 نقاط + أوسمة</span></div>
        </div>
        <div className="blind-hero-art" aria-hidden="true"><span>👁️</span><i>✍️</i><b>🙈</b><strong>?!</strong></div>
      </header>

      <div className="blind-how-grid">
        <div><b>1</b><span>اقرأ المهمة</span><small>موقف قصير وغريب وسهل التخمين.</small></div>
        <div><b>2</b><span>ابدأ الرسم</span><small>بعد أول لمسة يبدأ عدّ اختفاء الحبر.</small></div>
        <div><b>3</b><span>أكمل أعمى</span><small>اللوحة مخفية لكن كل ضربة محفوظة.</small></div>
        <div><b>4</b><span>اكشف وخمّن</span><small>تظهر الكارثة للجميع وتبدأ النقاط.</small></div>
      </div>

      <div className={`blind-lobby-grid ${self.isChef ? 'with-admin' : ''}`}>
        <div className="glass-card blind-room-card">
          <div className="blind-card-head"><div><span className="eyebrow">الغرفة</span><h2>{connectedPlayers.length} لاعب جاهز</h2></div><span className="live-badge"><i /> مباشر</span></div>
          <div className="blind-player-grid">
            {room.players.filter((player) => !player.isSpectator).map((player, index) => (
              <div className={`blind-player ${!player.connected ? 'offline' : ''}`} key={player.id}>
                <div className="blind-player-avatar">{avatarEmoji(index, player.nickname)}</div>
                <div><strong>{player.nickname}</strong><small>{player.isChef ? '👑 Admin' : player.connected ? 'جاهز للفوضى' : 'منقطع مؤقتًا'}</small></div>
                <i className={player.connected ? 'online' : ''} />
              </div>
            ))}
          </div>
          {!self.isChef && <div className="blind-waiting"><span>🙈</span><h3>بانتظار الأدمن</h3><p>حضّر يدك. مهارة الرسم لن تنقذك عندما تختفي الخطوط.</p></div>}
        </div>

        {self.isChef && (
          <aside className="glass-card blind-settings-card">
            <div className="blind-card-head"><div><span className="eyebrow">👑 إعدادات الأدمن</span><h2>درجة الفوضى</h2></div><span className="blind-pro-badge">PRO</span></div>

            <BlindRange label="وقت الرسم" value={settings.drawSeconds} min={20} max={180} unit="ث" onChange={(value) => patch('drawSeconds', value)} />
            <BlindRange label="وقت التخمين" value={settings.guessSeconds} min={10} max={90} unit="ث" onChange={(value) => patch('guessSeconds', value)} />
            <BlindRange label="وقت الكشف" value={settings.revealSeconds} min={3} max={15} unit="ث" onChange={(value) => patch('revealSeconds', value)} />

            <BlindSegments label="عدد الجولات" value={String(settings.rounds)} options={['1', '2', '3', '4', '5'].map((value) => [value, value])} onChange={(value) => patch('rounds', Number(value))} />
            <BlindSegments label="اختفاء الحبر بعد" value={String(settings.blindAfterMs)} options={[["700", '0.7ث'], ["1000", '1ث'], ["1500", '1.5ث'], ["2000", '2ث']]} onChange={(value) => patch('blindAfterMs', Number(value))} />
            <BlindSegments label="محاولات التخمين" value={String(settings.maxAttempts)} options={['1', '2', '3', '4', '5'].map((value) => [value, value])} onChange={(value) => patch('maxAttempts', Number(value))} />
            <BlindSegments label="حزمة الكلمات" value={settings.wordPack} options={[["mixed", 'متنوعة'], ["easy", 'سهلة'], ["chaos", 'فوضى 😂']]} onChange={(value) => patch('wordPack', value as BlindSettings['wordPack'])} />
            <BlindSegments label="شكل اللوحة" value={settings.canvasFormat} options={[["standard", 'عريضة'], ["square", 'مربعة']]} onChange={(value) => patch('canvasFormat', value as BlindSettings['canvasFormat'])} />

            <label className="blind-switch-row"><span><strong>تلميح الفئة</strong><small>مثل: حيوان، طعام، شيء… بدون كشف الإجابة.</small></span><input type="checkbox" checked={settings.showCategoryHint} onChange={(event) => patch('showCategoryHint', event.target.checked)} /><i /></label>

            <div className="blind-custom-prompts">
              <label>كلمات خاصة <small>اختياري</small></label>
              <textarea value={customPrompts} onChange={(event) => setCustomPrompts(event.target.value)} maxLength={6000} placeholder={'قطة | قطة تقود دبابة\nبيتزا | بيتزا تهرب من الفرن\nأو اكتب كلمة بسيطة فقط'} />
              <small>سطر لكل مهمة. الصيغة الأفضل: <b>الإجابة | المشهد المضحك</b>. إذا تركته فارغًا ستُستخدم الحزمة الجاهزة.</small>
            </div>

            <div className="blind-admin-actions">
              <button type="button" className="secondary-btn" onClick={saveSettings}>حفظ</button>
              <button type="button" className="primary-btn xl" disabled={!canStart || busy} onClick={start}>{busy ? 'تجهيز الفوضى...' : 'ابدأ Blind Drawing 🙈'}</button>
            </div>
            {!canStart && <p className="tiny-note">يلزم {room.minPlayers} لاعبين متصلين على الأقل.</p>}
          </aside>
        )}
      </div>
    </section>
  );
}

function BlindDrawingStage({ self, room, task, notify }: { self: Player; room: BlindState; task: Extract<BlindTask, { kind: 'blind-draw' }> | null; notify: (message: string) => void }) {
  const canvasRef = useRef<DrawingCanvasHandle | null>(null);
  const [busy, setBusy] = useState(false);
  const submitted = Boolean(task?.submitted || room.submittedPlayerIds?.includes(self.id));

  useEffect(() => setBusy(false), [task?.drawingId]);

  useEffect(() => {
    if (!task || submitted) return;
    const saveDraft = () => {
      try {
        const submission = canvasRef.current?.exportSubmission();
        if (submission) socket.emit('blind:drawingDraft', { value: submission.image, replay: submission.replay });
      } catch { /* canvas can be remounting */ }
    };
    const interval = window.setInterval(saveDraft, 1800);
    const beforeDeadline = Math.max(0, task.endsAt - Date.now() - 500);
    const finalTimer = window.setTimeout(saveDraft, beforeDeadline);
    return () => { window.clearInterval(interval); window.clearTimeout(finalTimer); };
  }, [task?.drawingId, task?.endsAt, submitted]);

  const submit = async () => {
    setBusy(true);
    try {
      const submission = canvasRef.current?.exportSubmission();
      if (!submission) throw new Error('تعذر قراءة الرسم.');
      const response = await emitBlindAck('blind:submitDrawing', { value: submission.image, replay: submission.replay });
      if (!response.ok) throw new Error(response.error || 'تعذر إرسال الرسم.');
      notify('تم حفظ الكارثة الفنية 🙈');
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };

  if (!task) return <BlindSpectator room={room} title="تجهيز مهمتك السرية" text="يتم اختيار مشهد لا يليق بأي فنان محترم…" />;

  if (submitted) {
    return (
      <section className="blind-stage">
        <BlindChefBar self={self} room={room} notify={notify} />
        <div className="glass-card blind-submitted-card"><div className="blind-success">🙈✓</div><span className="eyebrow">وصلت الرسمة</span><h1>ممنوع النظر حتى الكشف!</h1><p>تم حفظ الرسم كما هو. ننتظر بقية الفنانين ثم ستبدأ محاكمة الرسومات واحدة واحدة.</p><div className="blind-progress"><i style={{ width: `${Math.min(100, room.drawingSubmittedCount / Math.max(1, room.drawingExpectedCount) * 100)}%` }} /></div><strong>{room.drawingSubmittedCount} / {room.drawingExpectedCount} أرسلوا</strong></div>
      </section>
    );
  }

  return (
    <section className="blind-stage">
      <BlindChefBar self={self} room={room} notify={notify} />
      <div className="blind-round-head">
        <div><span>الجولة {task.round} من {task.totalRounds}</span><div className="blind-progress"><i style={{ width: `${task.round / Math.max(1, task.totalRounds) * 100}%` }} /></div></div>
        <div className="blind-head-stats"><span>✓ {room.drawingSubmittedCount}/{room.drawingExpectedCount}</span><Countdown endsAt={task.endsAt} totalSeconds={room.blindSettings.drawSeconds} hourglass warningSound activityLabel="الرسم" /></div>
      </div>

      <div className="blind-prompt-card glass-card">
        <div className="blind-prompt-icon">🎯</div>
        <div><span>ارسم هذا المشهد</span><h1>{task.prompt}</h1><p>الكلمة الأساسية التي يجب أن يفهمها الآخرون: <b>{task.answerLabel}</b> · الفئة: {task.category}</p></div>
        <div className="blind-warning"><b>{formatBlindDelay(task.blindAfterMs)}</b><small>بعد أول لمسة يختفي الحبر</small></div>
      </div>

      <div className="blind-canvas-frame">
        <div className="blind-canvas-tip"><span>🙈</span><p><b>كيف تعمل؟</b> ابدأ الرسم طبيعيًا. بعد أول ضربة سيبقى الحبر ظاهرًا للحظة قصيرة فقط، ثم ستكمل على لوحة لا ترى فيها شيئًا.</p></div>
        <DrawingCanvas key={task.drawingId} ref={canvasRef} canvasFormat={task.canvasFormat} blindAfterMs={task.blindAfterMs} blindLabel="الحبر اختفى… لا تتوقف!" />
      </div>

      <div className="blind-draw-actions"><span>💡 لا تحتاج رسمًا جميلًا؛ تحتاج ذاكرة مكانية وشجاعة.</span><button type="button" className="primary-btn xl" onClick={submit} disabled={busy}>{busy ? 'جاري الحفظ...' : 'إرسال الكارثة الفنية 🙈'}</button></div>
    </section>
  );
}

function BlindGuessStage({ self, room, task, spotlight, notify }: { self: Player; room: BlindState; task: Extract<BlindTask, { kind: 'blind-guess' }> | null; spotlight: BlindSpotlight | null; notify: (message: string) => void }) {
  const [guess, setGuess] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const currentSpotlight = spotlight?.drawingId === room.currentDrawingId ? spotlight : null;

  useEffect(() => { setGuess(''); setBusy(false); setFeedback(''); }, [room.currentDrawingId]);

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!task || !guess.trim() || busy || task.correct || task.attemptsUsed >= task.maxAttempts || task.isOwnDrawing) return;
    setBusy(true);
    try {
      const response = await emitBlindAck('blind:submitGuess', { drawingId: task.drawingId, value: guess.trim() });
      if (!response.ok) throw new Error(response.error || 'تعذر إرسال التخمين.');
      const message = response.message || (response.correct ? 'صحيح! 🎯' : 'ليست هي 😅');
      setFeedback(message);
      if (response.correct) notify(message);
      setGuess('');
    } catch (error) { notify(messageOf(error)); }
    finally { setBusy(false); }
  };

  if (!currentSpotlight) return <BlindSpectator room={room} title="إضاءة الرسمة التالية" text="يتم فتح المسرح للكارثة الفنية التالية…" />;

  const attemptsLeft = task ? Math.max(0, task.maxAttempts - task.attemptsUsed) : 0;
  const canGuess = Boolean(task && !self.isSpectator && !task.isOwnDrawing && !task.correct && attemptsLeft > 0);

  return (
    <section className="blind-stage blind-guess-stage">
      <BlindChefBar self={self} room={room} notify={notify} />
      <div className="blind-round-head">
        <div><span>فك الشفرة · الرسمة {room.currentDrawingIndex + 1} من {room.currentDrawingTotal}</span><div className="blind-progress"><i style={{ width: `${(room.currentDrawingIndex + 1) / Math.max(1, room.currentDrawingTotal) * 100}%` }} /></div></div>
        <div className="blind-head-stats"><span>🎯 {room.correctGuessCount}/{room.guessExpectedCount}</span>{room.guessEndsAt && <Countdown endsAt={room.guessEndsAt} totalSeconds={room.blindSettings.guessSeconds} hourglass warningSound activityLabel="التخمين" />}</div>
      </div>

      <div className="blind-spotlight-grid">
        <div className="glass-card blind-art-card">
          <div className="blind-art-meta"><span>🙈 رسمة {currentSpotlight.authorNickname}</span>{room.currentCategory && <b>تلميح: {room.currentCategory}</b>}</div>
          <div className={`blind-art-paper canvas-${room.blindSettings.canvasFormat}`}><img src={currentSpotlight.image} alt={`رسمة ${currentSpotlight.authorNickname}`} /></div>
          <div className="blind-art-caption">رُسمت بالكامل تقريبًا بدون رؤية الخطوط.</div>
        </div>

        <aside className="glass-card blind-guess-card">
          {self.isSpectator ? (
            <><div className="blind-big-icon">👁️</div><span className="eyebrow">وضع المشاهدة</span><h2>ماذا ترى أنت؟</h2><p>المشاهدون لا يؤثرون على النقاط، لكن يمكنك أن تخمّن بصمت وتضحك على النتائج.</p><div className="blind-live-counter">{room.correctGuessCount} عرفوها حتى الآن</div></>
          ) : task?.isOwnDrawing ? (
            <><div className="blind-big-icon">😈</div><span className="eyebrow">هذه كارثتك</span><h2>لا تقل لهم شيئًا!</h2><p>راقب كيف يحاول أصدقاؤك فهم ما فعلته وأنت لا ترى خطوطك.</p><div className="blind-live-counter">{room.correctGuessCount} / {room.guessExpectedCount} عرفوا الإجابة</div></>
          ) : task?.correct ? (
            <><div className="blind-big-icon">🎯</div><span className="eyebrow">إجابة صحيحة</span><h2>أنت فككت الشفرة!</h2><p>تم احتساب نقاطك حسب سرعة الإجابة. انتظر بقية اللاعبين.</p><div className="blind-live-counter success">{room.correctGuessCount} عرفوها حتى الآن</div></>
          ) : attemptsLeft <= 0 ? (
            <><div className="blind-big-icon">🤯</div><span className="eyebrow">انتهت المحاولات</span><h2>استسلم للكارثة.</h2><p>الحقيقة ستظهر بعد لحظات، مع أكثر التخمينات التي تستحق الضحك.</p><div className="blind-live-counter">{room.correctGuessCount} عرفوها حتى الآن</div></>
          ) : (
            <form onSubmit={submit}>
              <span className="eyebrow">ما الشيء الأساسي؟</span><h2>فك الشفرة</h2><p>لديك <b>{attemptsLeft}</b> محاولة. الأخطاء القريبة ستحصل على إشارة 🔥 بدون كشف الإجابة.</p>
              <input className="input blind-guess-input" value={guess} autoFocus maxLength={80} placeholder="اكتب تخمينك…" onChange={(event) => setGuess(event.target.value)} />
              {feedback && <div className={`blind-feedback ${feedback.includes('قريب') ? 'near' : ''}`}>{feedback}</div>}
              <button className="primary-btn xl full" disabled={!canGuess || busy || !guess.trim()}>{busy ? 'فحص التخمين...' : 'إرسال التخمين 🎯'}</button>
              <div className="blind-attempt-dots">{Array.from({ length: task?.maxAttempts ?? 0 }, (_, index) => <i key={index} className={index < (task?.attemptsUsed ?? 0) ? 'used' : ''} />)}<span>{attemptsLeft} متبقية</span></div>
            </form>
          )}
        </aside>
      </div>
    </section>
  );
}

function BlindRevealStage({ self, room, reveal, spotlight, notify }: { self: Player; room: BlindState; reveal: BlindRevealPayload | null; spotlight: BlindSpotlight | null; notify: (message: string) => void }) {
  const data = reveal?.drawingId === room.currentDrawingId ? reveal : null;
  const [replayKey, setReplayKey] = useState(0);
  const hasReplay = Boolean(data?.replay?.strokes.length);
  const replaySpeed = data?.replay ? Math.max(2, Math.ceil((data.replay.durationMs / 1000) / Math.max(2, room.blindSettings.revealSeconds - 1))) : 2;

  useEffect(() => setReplayKey((value) => value + 1), [room.currentDrawingId]);
  if (!data) {
    return <BlindSpectator room={room} title="لحظة الحقيقة…" text={spotlight ? 'جارٍ كشف الكلمة والتخمينات.' : 'يتم تحميل نتيجة الرسمة.'} />;
  }

  return (
    <section className="blind-stage blind-reveal-stage">
      <BlindChefBar self={self} room={room} notify={notify} />
      <div className="blind-reveal-banner glass-card"><span>✨ لحظة الحقيقة</span><h1>{data.answer}</h1><p>المهمة الأصلية كانت: <b>«{data.prompt}»</b></p>{room.revealEndsAt && <Countdown endsAt={room.revealEndsAt} totalSeconds={room.blindSettings.revealSeconds} />}</div>

      <div className="blind-reveal-grid">
        <div className="glass-card blind-reveal-art"><div className="blind-art-meta"><span>🎨 الفنان الأعمى: {data.authorNickname}</span><b>الجولة {data.round}</b></div><div className={`blind-art-paper blind-replay-paper canvas-${room.blindSettings.canvasFormat}`}>{hasReplay && data.replay ? <BlindDrawingReplay key={`${data.drawingId}-${replayKey}`} replay={data.replay} finalImage={data.image} speed={replaySpeed} canvasFormat={room.blindSettings.canvasFormat} /> : <img src={data.image} alt="الرسم المكشوف" />}</div>{hasReplay && <div className="blind-replay-controls"><span>🎬 إعادة الكارثة ضربة بضربة · {replaySpeed}×</span><button type="button" className="ghost-btn" onClick={() => setReplayKey((value) => value + 1)}>↻ إعادة المشاهدة</button></div>}</div>
        <aside className="glass-card blind-reveal-stats">
          <h2>🎯 من فك الشفرة؟</h2>
          {data.correctGuessers.length ? <div className="blind-correct-list">{data.correctGuessers.map((winner, index) => <div key={winner.playerId}><span>{index === 0 ? '⚡' : '✓'}</span><strong>{winner.nickname}</strong><b>+{winner.points}</b></div>)}</div> : <div className="blind-none-correct">😵‍💫<strong>ولا أحد!</strong><span>الفنان انتصر على المنطق.</span></div>}
          <div className="blind-drawer-bonus"><span>🎨</span><p>الفنان يحصل على <b>+60</b> لكل شخص فهم الرسمة، و<b>+120</b> إضافية إذا فهمها نصف اللاعبين على الأقل.</p></div>
        </aside>
      </div>

      {data.funnyGuesses.length > 0 && <div className="glass-card blind-funny-guesses"><div><span className="eyebrow">😂 سجل التحقيقات الفاشلة</span><h2>ماذا ظنوا أنها؟</h2></div><div className="blind-funny-grid">{data.funnyGuesses.map((item, index) => <div key={`${item.playerId}-${index}`}><span>“</span><strong>{item.text}</strong><small>{item.nickname}</small></div>)}</div></div>}
    </section>
  );
}

function BlindResults({ self, room, notify }: { self: Player; room: BlindState; notify: (message: string) => void }) {
  const [busy, setBusy] = useState(false);
  const scores = [...room.scores].sort((a, b) => b.score - a.score || b.correctGuesses - a.correctGuesses);
  const podium = scores.slice(0, 3);

  const reset = async () => {
    setBusy(true);
    try {
      const response = await emitBlindAck('chef:blindReset', {});
      if (!response.ok) throw new Error(response.error || 'تعذر العودة إلى الغرفة.');
    } catch (error) { notify(messageOf(error)); setBusy(false); }
  };

  return (
    <section className="blind-stage blind-results-stage">
      <header className="blind-results-hero"><span className="eyebrow">🏁 انتهت الفوضى</span><h1>قاعة مشاهير الرسم الأعمى</h1><p>المهارة كانت اختيارية. الشجاعة لم تكن كذلك.</p></header>

      <div className="blind-podium">
        {podium.map((score, index) => <div className={`glass-card blind-podium-card place-${index + 1}`} key={score.playerId}><span className="blind-medal">{index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}</span><div className="blind-podium-avatar">{avatarEmoji(index, score.nickname)}</div><strong>{score.nickname}</strong><b>{score.score}</b><small>نقطة</small><p>{score.correctGuesses} تخمين صحيح · {score.drawingsRecognized} فهموا رسوماته</p></div>)}
      </div>

      {room.awards.length > 0 && <div className="blind-awards-grid">{room.awards.map((award) => <div className="glass-card blind-award" key={award.key}><span>{award.emoji}</span><div><small>{award.title}</small><strong>{award.nickname}</strong><p>{award.description}</p></div></div>)}</div>}

      <div className="glass-card blind-score-table">
        <div className="blind-card-head"><div><span className="eyebrow">الترتيب الكامل</span><h2>لوحة النقاط</h2></div><span>{room.totalRounds} جولات</span></div>
        <div className="blind-score-rows">{scores.map((score, index) => <div key={score.playerId}><b>#{index + 1}</b><span className="blind-score-avatar">{avatarEmoji(index, score.nickname)}</span><strong>{score.nickname}</strong><small>🎯 {score.correctGuesses}</small><small>🎨 {score.drawingsRecognized}</small><small>⚡ {score.fastestGuesses}</small><em>{score.score}</em></div>)}</div>
      </div>

      {self.isChef && <div className="blind-results-actions"><button type="button" className="primary-btn xl" onClick={reset} disabled={busy}>{busy ? 'جاري تجهيز الغرفة...' : 'العودة للغرفة ولعبة جديدة ↻'}</button></div>}
    </section>
  );
}

function BlindChefBar({ self, room, notify }: { self: Player; room: BlindState; notify: (message: string) => void }) {
  const [busy, setBusy] = useState<'skip' | 'reset' | null>(null);
  if (!self.isChef) return null;

  const skip = async () => {
    setBusy('skip');
    try {
      const response = await emitBlindAck('chef:blindSkip', { phase: room.phase, round: room.round, drawingId: room.currentDrawingId });
      if (!response.ok) throw new Error(response.error || 'تعذر الانتقال.');
    } catch (error) { notify(messageOf(error)); }
    finally { setBusy(null); }
  };

  const reset = async () => {
    if (!window.confirm('إنهاء Blind Drawing الحالية والعودة إلى غرفة الانتظار؟')) return;
    setBusy('reset');
    try {
      const response = await emitBlindAck('chef:blindReset', {});
      if (!response.ok) throw new Error(response.error || 'تعذر إنهاء اللعبة.');
    } catch (error) { notify(messageOf(error)); }
    finally { setBusy(null); }
  };

  const skipLabel = room.phase === 'blind_drawing' ? 'إنهاء الرسم الآن' : room.phase === 'blind_guessing' ? 'كشف الإجابة الآن' : 'الرسمة التالية الآن';
  return <div className="glass-card blind-chef-bar"><div><span>👑</span><p><strong>تحكم الأدمن</strong><small>يمكنك تجاوز المؤقت بدون تعطيل الجولة.</small></p></div><div><button type="button" className="ghost-btn" onClick={reset} disabled={busy !== null}>{busy === 'reset' ? 'جاري الإنهاء...' : 'إنهاء اللعبة'}</button><button type="button" className="secondary-btn" onClick={skip} disabled={busy !== null}>{busy === 'skip' ? 'لحظة...' : skipLabel}</button></div></div>;
}

function BlindSpectator({ room, title, text }: { room: BlindState; title: string; text: string }) {
  return <section className="blind-stage"><div className="glass-card blind-spectator-card"><div className="blind-spectator-orbit"><span>🙈</span><i /><i /><i /></div><span className="eyebrow">Blind Drawing</span><h1>{title}</h1><p>{text}</p><div className="blind-live-counter">{room.players.filter((player) => player.connected && !player.isSpectator).length} لاعب في الجولة</div></div></section>;
}

function BlindDrawingReplay({ replay, finalImage, speed, canvasFormat }: { replay: DrawingReplay; finalImage: string; speed: number; canvasFormat: 'standard' | 'square' }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !replay.strokes.length) { setDone(true); return; }
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    setDone(false);
    const ratio = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round(rect.width * ratio));
    canvas.height = Math.max(1, Math.round(rect.height * ratio));
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const startedAt = performance.now();
    const total = Math.max(250, replay.durationMs);
    const effectiveSpeed = Math.max(.1, speed);
    let previousVirtualTime = -1;
    const render = (now: number) => {
      const virtualTime = Math.min(total, (now - startedAt) * effectiveSpeed);
      for (const stroke of replay.strokes) drawBlindReplayDelta(ctx, canvas, stroke, previousVirtualTime, virtualTime);
      previousVirtualTime = virtualTime;
      if (virtualTime >= total) { setDone(true); return; }
      frameRef.current = requestAnimationFrame(render);
    };
    frameRef.current = requestAnimationFrame(render);
    return () => { if (frameRef.current !== null) cancelAnimationFrame(frameRef.current); };
  }, [replay, speed]);

  return <div className={`blind-replay-frame canvas-${canvasFormat}`}><canvas ref={canvasRef} className="blind-replay-canvas" />{!done && <span className="blind-replay-badge">🙈 ذاكرة اليد · {speed}×</span>}{done && <span className="blind-replay-badge done">✓ هذه هي الكارثة النهائية</span>}{!replay.strokes.length && <img src={finalImage} alt="الرسم النهائي" />}</div>;
}

function drawBlindReplayDelta(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, stroke: ReplayStroke, fromTime: number, toTime: number) {
  const tool = stroke.tool ?? (stroke.eraser ? 'eraser' : 'pencil');
  if (tool !== 'pencil' && tool !== 'eraser') {
    const finishedAt = stroke.points.at(-1)?.t ?? 0;
    if (finishedAt > fromTime && finishedAt <= toTime) drawBlindReplayAction(ctx, canvas, stroke, stroke.points);
    return;
  }
  const selected: number[] = [];
  for (let index = 0; index < stroke.points.length; index++) {
    const time = stroke.points[index].t;
    if (time > fromTime && time <= toTime) selected.push(index);
  }
  if (!selected.length) return;
  const first = selected[0];
  const last = selected.at(-1)!;
  drawBlindReplayAction(ctx, canvas, stroke, stroke.points.slice(Math.max(0, first - 1), last + 1));
}

function drawBlindReplayAction(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, stroke: ReplayStroke, points: ReplayStroke['points']) {
  if (!points.length) return;
  const tool = stroke.tool ?? (stroke.eraser ? 'eraser' : 'pencil');
  const start = points[0];
  const end = points.at(-1) ?? start;
  const rect = canvas.getBoundingClientRect();
  const scale = ((canvas.width / Math.max(1, rect.width)) + (canvas.height / Math.max(1, rect.height))) / 2;
  if (tool === 'fill') { floodFillBlindReplay(ctx, canvas, start.x, start.y, stroke.color, stroke.opacity ?? 1); return; }
  ctx.save();
  ctx.globalAlpha = stroke.opacity ?? 1;
  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : stroke.color;
  ctx.fillStyle = tool === 'eraser' ? '#ffffff' : stroke.color;
  ctx.lineWidth = stroke.size * scale;
  if (tool === 'line') {
    ctx.beginPath(); ctx.moveTo(start.x * canvas.width, start.y * canvas.height); ctx.lineTo(end.x * canvas.width, end.y * canvas.height); ctx.stroke(); ctx.restore(); return;
  }
  if (tool === 'rect' || tool === 'rect-fill') {
    const x = Math.min(start.x, end.x) * canvas.width, y = Math.min(start.y, end.y) * canvas.height;
    const w = Math.abs(end.x - start.x) * canvas.width, h = Math.abs(end.y - start.y) * canvas.height;
    tool === 'rect-fill' ? ctx.fillRect(x, y, w, h) : ctx.strokeRect(x, y, w, h); ctx.restore(); return;
  }
  if (tool === 'ellipse' || tool === 'ellipse-fill') {
    const cx = ((start.x + end.x) / 2) * canvas.width, cy = ((start.y + end.y) / 2) * canvas.height;
    const rx = Math.abs(end.x - start.x) * canvas.width / 2, ry = Math.abs(end.y - start.y) * canvas.height / 2;
    ctx.beginPath(); ctx.ellipse(cx, cy, Math.max(.5, rx), Math.max(.5, ry), 0, 0, Math.PI * 2);
    tool === 'ellipse-fill' ? ctx.fill() : ctx.stroke(); ctx.restore(); return;
  }
  ctx.beginPath(); ctx.moveTo(start.x * canvas.width, start.y * canvas.height);
  if (points.length === 1) { ctx.arc(start.x * canvas.width, start.y * canvas.height, ctx.lineWidth / 2, 0, Math.PI * 2); ctx.fill(); }
  else { for (let index = 1; index < points.length; index++) ctx.lineTo(points[index].x * canvas.width, points[index].y * canvas.height); ctx.stroke(); }
  ctx.restore();
}

function floodFillBlindReplay(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, nx: number, ny: number, color: string, opacity: number) {
  const width = canvas.width, height = canvas.height;
  const x = Math.max(0, Math.min(width - 1, Math.round(nx * (width - 1))));
  const y = Math.max(0, Math.min(height - 1, Math.round(ny * (height - 1))));
  const image = ctx.getImageData(0, 0, width, height), data = image.data;
  const startIndex = (y * width + x) * 4;
  const target = [data[startIndex], data[startIndex + 1], data[startIndex + 2], data[startIndex + 3]];
  const hex = color.replace('#', ''), number = Number.parseInt(hex, 16);
  const fill = { r: (number >> 16) & 255, g: (number >> 8) & 255, b: number & 255, a: Math.max(0, Math.min(1, opacity)) };
  if (Math.abs(target[0] - fill.r) < 3 && Math.abs(target[1] - fill.g) < 3 && Math.abs(target[2] - fill.b) < 3) return;
  const match = (i: number) => Math.abs(data[i] - target[0]) < 8 && Math.abs(data[i + 1] - target[1]) < 8 && Math.abs(data[i + 2] - target[2]) < 8 && Math.abs(data[i + 3] - target[3]) < 8;
  const stack = [y * width + x], visited = new Uint8Array(width * height);
  let processed = 0, pos: number | undefined;
  while ((pos = stack.pop()) !== undefined && processed < Math.min(width * height, 2_500_000)) {
    if (visited[pos]) continue; visited[pos] = 1;
    const px = pos % width, py = Math.floor(pos / width), i = pos * 4;
    if (!match(i)) continue;
    data[i] = Math.round(fill.r * fill.a + data[i] * (1 - fill.a)); data[i + 1] = Math.round(fill.g * fill.a + data[i + 1] * (1 - fill.a)); data[i + 2] = Math.round(fill.b * fill.a + data[i + 2] * (1 - fill.a)); data[i + 3] = 255; processed++;
    if (px > 0) stack.push(pos - 1); if (px + 1 < width) stack.push(pos + 1); if (py > 0) stack.push(pos - width); if (py + 1 < height) stack.push(pos + width);
  }
  ctx.putImageData(image, 0, 0);
}

function BlindRange({ label, value, min, max, unit, onChange }: { label: string; value: number; min: number; max: number; unit: string; onChange: (value: number) => void }) {
  return <label className="blind-range"><span><strong>{label}</strong><b>{value}{unit}</b></span><input type="range" min={min} max={max} value={value} onChange={(event) => onChange(Number(event.target.value))} /></label>;
}

function BlindSegments({ label, value, options, onChange }: { label: string; value: string; options: string[][]; onChange: (value: string) => void }) {
  return <div className="blind-segments-row"><strong>{label}</strong><div>{options.map(([id, text]) => <button key={id} type="button" className={value === id ? 'active' : ''} onClick={() => onChange(id)}>{text}</button>)}</div></div>;
}

function avatarEmoji(index: number, name: string) {
  const pool = ['🤪', '😎', '🤓', '🥸', '😺', '👽', '🤖', '🦊', '🐸', '🦄', '🐵', '🧙'];
  let hash = index;
  for (const char of name) hash = (hash * 31 + char.codePointAt(0)!) >>> 0;
  return pool[hash % pool.length];
}

function messageOf(error: unknown) {
  return error instanceof Error ? error.message : 'حدث خطأ غير متوقع.';
}

function emitBlindAck(event: string, payload: unknown): Promise<BlindAck> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let ackTimer: number | null = null;
    let connectTimer: number | null = null;

    const cleanup = () => {
      if (ackTimer !== null) window.clearTimeout(ackTimer);
      if (connectTimer !== null) window.clearTimeout(connectTimer);
      socket.off('connect', send);
    };
    const finish = (response: BlindAck) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(response);
    };
    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(message));
    };
    const send = () => {
      if (settled) return;
      socket.off('connect', send);
      if (connectTimer !== null) {
        window.clearTimeout(connectTimer);
        connectTimer = null;
      }
      ackTimer = window.setTimeout(
        () => fail('الاتصال بطيء جدًا ولم يصل تأكيد الخادم بعد. انتظر لحظات ثم حاول مجددًا.'),
        30_000
      );
      socket.emit(event, payload, (response: BlindAck) => finish(response));
    };

    if (socket.connected) {
      send();
    } else {
      socket.on('connect', send);
      connectTimer = window.setTimeout(
        () => fail('جاري إعادة الاتصال بالخادم. انتظر حتى تظهر حالة «متصل» ثم حاول مرة أخرى.'),
        12_000
      );
      socket.connect();
    }
  });
}

function formatBlindDelay(ms: number) {
  const seconds = ms / 1000;
  return `${Number.isInteger(seconds) ? seconds.toFixed(0) : seconds.toFixed(1)} ثانية`;
}
